import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Search, Coffee, ChevronDown, ChevronUp, Camera, Loader2, Download, Upload, Plus, X } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import ImportExcelModal from '../../components/shared/ImportExcelModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ETIQUETAS = ['Todas', 'Base obligatoria', 'Energeticas', 'Funcionales', 'Recuperacion'];
const ETIQUETAS_FORM = ['Base obligatoria', 'Energeticas', 'Funcionales', 'Recuperacion'];

const TAG_COLORS = {
  'Base obligatoria': 'bg-blue-100 text-blue-700',
  'Energeticas': 'bg-amber-100 text-amber-700',
  'Funcionales': 'bg-violet-100 text-violet-700',
  'Recuperacion': 'bg-emerald-100 text-emerald-700',
};

const NutrientBar = ({ label, value, max, color }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-gray-500 w-10 text-right font-medium">{label}</span>
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] font-bold text-gray-700 w-10">{value}g</span>
    </div>
  );
};

const ImageUploader = ({ itemId, currentUrl, collection, onUpdated }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/diet-image/${collection}/${itemId}`, formData);
      onUpdated(res.data.image_url);
    } catch (error) { console.error('Upload failed:', error); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="relative group w-full aspect-[4/3] rounded-xl overflow-hidden bg-gray-100">
      {currentUrl ? (
        <img src={currentUrl.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${currentUrl}` : currentUrl}
          alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-gray-300"><Coffee size={32} /></div>
      )}
      <button onClick={() => fileRef.current?.click()}
        className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
        data-testid={`upload-img-${itemId}`}>
        {uploading ? <Loader2 size={20} className="text-white animate-spin" /> : <Camera size={20} className="text-white" />}
      </button>
      <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
    </div>
  );
};

const EMPTY_BEBIDA = { nombre: '', descripcion: '', categoria: 'Bebida', unidad: 'ml', gramos_por_unidad: 240, calorias: 0, grasa: 0, proteinas: 0, carbohidratos: 0, beneficios: '', etiqueta: 'Base obligatoria' };

const CreateBebidaModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ ...EMPTY_BEBIDA });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (!form.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/bebidas`, form);
      toast.success('Bebida creada exitosamente');
      onCreated(res.data);
      onClose();
    } catch (error) { toast.error('Error al crear bebida'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="create-bebida-modal">
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-black text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Nueva Bebida</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Nombre *</label>
            <Input value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Nombre de la bebida" className="mt-1" data-testid="bebida-nombre" />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Descripcion</label>
            <Input value={form.descripcion} onChange={e => set('descripcion', e.target.value)} placeholder="Descripcion" className="mt-1" data-testid="bebida-descripcion" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Unidad</label>
              <select value={form.unidad} onChange={e => set('unidad', e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:border-[#467322] outline-none">
                <option value="ml">ml</option><option value="g">g</option><option value="oz">oz</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Gramos/Unidad</label>
              <Input type="number" value={form.gramos_por_unidad} onChange={e => set('gramos_por_unidad', Number(e.target.value))} className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Calorias</label>
              <Input type="number" value={form.calorias} onChange={e => set('calorias', Number(e.target.value))} className="mt-1" data-testid="bebida-calorias" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Grasa</label>
              <Input type="number" value={form.grasa} onChange={e => set('grasa', Number(e.target.value))} className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Proteinas</label>
              <Input type="number" value={form.proteinas} onChange={e => set('proteinas', Number(e.target.value))} className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Carbos</label>
              <Input type="number" value={form.carbohidratos} onChange={e => set('carbohidratos', Number(e.target.value))} className="mt-1" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Etiqueta</label>
            <select value={form.etiqueta} onChange={e => set('etiqueta', e.target.value)}
              className="mt-1 w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:border-[#467322] outline-none" data-testid="bebida-etiqueta">
              {ETIQUETAS_FORM.map(et => <option key={et} value={et}>{et}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Beneficios</label>
            <Input value={form.beneficios} onChange={e => set('beneficios', e.target.value)} placeholder="Beneficios de la bebida" className="mt-1" data-testid="bebida-beneficios" />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50">Cancelar</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-[#467322] text-white font-bold text-sm hover:bg-[#3A601C] transition-colors disabled:opacity-50"
            data-testid="bebida-save-btn">
            {saving ? <Loader2 size={14} className="inline animate-spin mr-1" /> : null} Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminBebidasPage = () => {
  const [bebidas, setBebidas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEtiqueta, setFilterEtiqueta] = useState('Todas');
  const [expandedId, setExpandedId] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const fetchBebidas = useCallback(() => {
    axios.get(`${API}/admin/bebidas`).then(res => setBebidas(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchBebidas(); }, [fetchBebidas]);

  const updateImage = (id, url) => {
    setBebidas(prev => prev.map(b => b.id === id ? { ...b, image_url: url } : b));
  };

  const exportData = () => {
    const blob = new Blob([JSON.stringify(bebidas, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bebidas_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = bebidas.filter(b => {
    const matchSearch = !search || b.nombre?.toLowerCase().includes(search.toLowerCase());
    const matchEtiqueta = filterEtiqueta === 'Todas' || b.etiqueta === filterEtiqueta;
    return matchSearch && matchEtiqueta;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#467322]" size={32} /></div>;

  return (
    <div data-testid="admin-bebidas-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Bebidas</h1>
          <p className="text-gray-500 text-sm mt-1">{filtered.length} de {bebidas.length} bebidas</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowImport(true)}
            className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-[#467322] hover:text-[#467322] transition-all shadow-sm"
            data-testid="import-bebidas-btn">
            <Upload size={15} /><span className="hidden sm:inline">Importar</span>
          </button>
          <button onClick={exportData}
            className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-[#467322] hover:text-[#467322] transition-all shadow-sm"
            data-testid="export-bebidas-btn">
            <Download size={15} /><span className="hidden sm:inline">Exportar</span>
          </button>
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#467322] text-white text-sm font-bold hover:bg-[#3A601C] transition-colors shadow-sm shadow-green-900/20"
            data-testid="new-bebida-btn">
            <Plus size={15} /><span className="hidden sm:inline">Nueva</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-5">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Buscar bebida..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-gray-50 border-transparent focus:bg-white focus:border-[#467322] rounded-xl h-10" data-testid="search-bebidas" />
          </div>
          <select value={filterEtiqueta} onChange={e => setFilterEtiqueta(e.target.value)}
            className="h-10 px-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:border-[#467322] outline-none" data-testid="filter-etiqueta-bebidas">
            {ETIQUETAS.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map(bebida => {
          const isExpanded = expandedId === bebida.id;
          return (
            <div key={bebida.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-testid={`bebida-${bebida.id}`}>
              <ImageUploader itemId={bebida.id} currentUrl={bebida.image_url} collection="bebidas" onUpdated={url => updateImage(bebida.id, url)} />
              <button onClick={() => setExpandedId(isExpanded ? null : bebida.id)}
                className="w-full text-left p-4 hover:bg-gray-50/50 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{bebida.nombre}</p>
                    <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{bebida.descripcion}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {bebida.etiqueta && (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${TAG_COLORS[bebida.etiqueta] || 'bg-gray-100 text-gray-600'}`}>{bebida.etiqueta}</span>
                    )}
                    {isExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <div className="text-center">
                    <p className="text-lg font-black text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{bebida.calorias}</p>
                    <p className="text-[10px] text-gray-400 font-medium">kcal</p>
                  </div>
                  <div className="flex-1 space-y-1">
                    <NutrientBar label="Grasa" value={bebida.grasa} max={25} color="bg-amber-400" />
                    <NutrientBar label="Prot" value={bebida.proteinas} max={25} color="bg-rose-400" />
                    <NutrientBar label="Carbs" value={bebida.carbohidratos} max={35} color="bg-blue-400" />
                  </div>
                </div>
              </button>
              {isExpanded && (
                <div className="border-t border-gray-100 p-4 animate-fade-in">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Unidad</p>
                      <p className="font-semibold text-gray-700">{bebida.gramos_por_unidad} {bebida.unidad}</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Beneficios</p>
                      <p className="font-semibold text-gray-700">{bebida.beneficios}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {filtered.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <Coffee size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-semibold">No se encontraron bebidas</p>
        </div>
      )}

      {showImport && <ImportExcelModal collection="bebidas" onClose={() => setShowImport(false)} onImported={fetchBebidas} />}
      {showCreate && <CreateBebidaModal onClose={() => setShowCreate(false)} onCreated={(b) => setBebidas(prev => [b, ...prev])} />}
    </div>
  );
};

export default AdminBebidasPage;
